import React, { useContext, useState } from 'react';
import ResultTable from '../pages/result/ResultTable';
import StatusIndicator from './StatusIndicator';
import { ABContext } from '../contexts/ABContext';
import { finishABTest } from '../services/ABServices';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import Button from 'Components/DesignSystem/Button/Button';
import Dialog from 'Components/DesignSystem/Dialog/Dialog';
import Typography from 'Components/DesignSystem/Typography/Typography';

type ABPromoteModalProps = {
    onCancel: () => void
}

/**
 * Stop & Promote — the only path to promoting a model (`okf/design-system/e2e.md`
 * § 161, flow in `okf/abtesting/e2e.md` § 5.4).
 *
 * **`finishABTest(testId, modelId)` remains the single POST**, and it both stops the
 * test and promotes the chosen model. The success/failure branches, the two toast
 * strings including the apostrophe-less "Could'nt", and `setTestData(res?.data)` on
 * success are all unchanged — as is the commented-out `promoteModel` block below
 * `handlePromoteModel`, which documents a standalone promote endpoint that has never
 * been reachable.
 *
 * **The submit button is now busy-locked.** It was a plain `<button>` gated only on
 * `!selectedModelId`, so a double click fired `finishABTest` twice — and this is the
 * request that stops a live test and reassigns 100% of its traffic. The second call
 * would hit a test already stopped.
 *
 * **§ 209 (2026-09-05):** the busy lock wears the DS loading state (spinner +
 * aria-busy + wait cursor), and while promoting EVERY close route is shut —
 * Cancel was already disabled, and X/Escape now route through the same guard —
 * because the call is not abortable: a mid-flight close would only hide the
 * dialog while the stop-and-promote still lands. `finally` resets the flag, so
 * the guard lifts on failure (success unmounts via `onCancel`).
 *
 * The shell was a hand-rolled `tw-fixed tw-inset-0` overlay at `tw-w-[65%]` with its
 * own `#f4f5f7` header band: no `role="dialog"`, no accessible name, no focus trap, no
 * Escape.
 */
const ABPromoteModal = ({ onCancel }: ABPromoteModalProps) => {
    const abContext = useContext(ABContext);
    const { testData, selectedModelId, setTestData } = abContext
    const [promoting, setPromoting] = useState(false);

    const handlePromoteModel = async () => {
        setPromoting(true);
        try {
            const res = await finishABTest(testData?.id, selectedModelId)
            if (res?.done) {
                setTestData(res?.data)
                toast.success(res?.message ?? "Test stopped successfully")
                onCancel()
            }
            else {
                res && res?.message ? toast.info(res?.message) : toast.info("Could'nt stop the test and model promotion failed!")
            }
        } catch (error) {
            console.log("EROR", error)

            toast.info("Could'nt stop the test and model Promotion failed!")
        } finally {
            /*
              `onCancel` unmounts this modal on the success path, so guard the state
              write. Not a ref-based mounted check: `onCancel` is called synchronously
              inside the `try`, so by the time `finally` runs React has already
              scheduled the unmount — the flag is only read again if we are still here,
              i.e. on a failure.
            */
            setPromoting(false);
        }
        // try {
        //     const res = await promoteModel(testData?.id, selectedModelId)
        //     if (res?.done) {
        //         onCancel()
        //         toast.success("Model Promotion Successful")
        //     }
        //     else toast.info(res?.message)
        // } catch (error) {
        //     toast.info("Model Promotion Failed")
        // }
    }

    /* One guard covers every close route — Cancel, the X, Escape (Dialog routes
       all three through `onClose`). See the docblock. */
    const guardedClose = () => {
        if (promoting) return;
        onCancel();
    };

    return (
        <Dialog
            isOpen
            onClose={guardedClose}
            title="Stop A/B test and Promote model"
            tone="warning"
            size="lg"
            data-testid="ab-promote-modal"
            footer={
                <div className="tw-flex tw-items-center tw-justify-end tw-gap-2">
                    <Button
                        variant="secondary"
                        onClick={guardedClose}
                        data-testid="ab-promote-cancel-btn"
                        disabled={promoting}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        data-testid="components-abpromotemodal-button-1"
                        onClick={handlePromoteModel}
                        /* `!selectedModelId` was the only gate; `loading` (§ 209) is
                           what stops a double click firing `finishABTest` twice
                           against a test the first call has already stopped. */
                        disabled={!selectedModelId}
                        loading={promoting}
                    >
                        {promoting ? 'Stopping...' : 'Stop & Promote'}
                    </Button>
                </div>
            }
        >
            <div className="tw-flex tw-items-center tw-justify-between">
                <StatusIndicator message={testData?.test_recommendation} data-testid="ab-promote-recommendation" />
                <Typography variant="sub-title-label" as="p" className="tw-text-ink-tertiary">
                    {'MLP'}:
                    <span className="tw-text-ink tw-ms-1">{`${testData?.win_threshold}% ${testData?.metric}`}</span>
                </Typography>
            </div>
            <div className="tw-mt-4">
                <Typography variant="title-header" as="h3" className="tw-text-ink">
                    Select model to promote
                </Typography>
                <div className="tw-mt-4">
                    <ResultTable checkBox={true} />
                </div>
            </div>
        </Dialog>
    );
}

export default ABPromoteModal;